import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export default function MenuAdmin() {
  const [menu, setMenu]                 = useState([]);
  const [toast, setToast]               = useState('');
  const [editItem, setEditItem]         = useState(null);
  const [editCat, setEditCat]           = useState(null);
  const [editVariants, setEditVariants] = useState(null);
  const [editModGroups, setEditModGroups] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [search, setSearch]             = useState('');
  const [filterCat, setFilterCat]       = useState('all');
  const [filterAvail, setFilterAvail]   = useState('all');
  const token = (window.__cafeApiToken || localStorage.getItem('cafepos_token'));

  const load = useCallback(async () => {
    const data = await api.getMenuAll();
    setMenu(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 2500); }

  async function toggleAvailable(item) {
    await api.updateMenuItem(item.id, { available: item.available ? 0 : 1 });
    showToast(item.available ? `"${item.name}" wyłączono` : `"${item.name}" włączono`);
    load();
  }

  async function saveItem(form) {
    setSaving(true);
    try {
      const vatRate = parseFloat(form.vat_rate ?? 23);
      if (form.id) {
        await api.updateMenuItem(form.id, { name: form.name, price: parseFloat(form.price), category_id: parseInt(form.category_id), item_type: form.item_type, vat_rate: vatRate });
        showToast('Zapisano zmiany');
      } else {
        await fetch('/api/menu/items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: form.name, price: parseFloat(form.price), category_id: parseInt(form.category_id), item_type: form.item_type, vat_rate: vatRate }),
        });
        showToast(`Dodano "${form.name}"`);
      }
      setEditItem(null); load();
    } catch (err) { showToast('Błąd: ' + err.message); }
    finally { setSaving(false); }
  }

  async function deleteItem(item) {
    if (!window.confirm(`Usunąć "${item.name}"? Zniknie ze wszystkich list (produktowej i panelu admina), ale historia zamówień z tym produktem zostanie zachowana.`)) return;
    await fetch(`/api/menu/items/${item.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    showToast(`Usunięto "${item.name}"`); load();
  }

  async function saveCat(form) {
    setSaving(true);
    try {
      if (form.id) {
        await fetch(`/api/menu/categories/${form.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: form.name, sort: parseInt(form.sort), color: form.color }),
        });
      } else {
        await fetch('/api/menu/categories', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: form.name, sort: parseInt(form.sort) || 99, color: form.color }),
        });
      }
      showToast('Zapisano'); setEditCat(null); load();
    } catch (err) { showToast('Błąd: ' + err.message); }
    finally { setSaving(false); }
  }

  async function deleteCat(cat) {
    if (!window.confirm(`Usunąć kategorię "${cat.name}"? Usunie też wszystkie jej pozycje!`)) return;
    await fetch(`/api/menu/categories/${cat.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    showToast(`Usunięto kategorię "${cat.name}"`); load();
  }

  // Filtrowanie
  const filteredMenu = menu.map(cat => {
    if (filterCat !== 'all' && cat.id !== parseInt(filterCat)) return null;
    const items = (cat.items || []).filter(item => {
      const matchSearch = search === '' || item.name.toLowerCase().includes(search.toLowerCase());
      const matchAvail  = filterAvail === 'all'
        || (filterAvail === 'available' && item.available)
        || (filterAvail === 'unavailable' && !item.available);
      return matchSearch && matchAvail;
    });
    if (items.length === 0 && search !== '') return null;
    return { ...cat, items };
  }).filter(Boolean);

  const totalItems = filteredMenu.reduce((s, c) => s + c.items.length, 0);

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 500 }}>Zarządzanie menu</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-outline" onClick={() => setEditCat({ name: '', sort: menu.length + 1 })}>+ Kategoria</button>
          <button className="btn-primary" style={{ width: 'auto', padding: '9px 16px' }}
            onClick={() => setEditItem({ name: '', price: '', category_id: menu[0]?.id || '' })}>
            + Pozycja
          </button>
        </div>
      </div>

      {/* Pasek filtrowania */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center', background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '12px 16px' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Szukaj po nazwie..."
          style={{ flex: 2, minWidth: 160, padding: '8px 12px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--bg)', color: 'var(--text)' }}
        />
        <select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          style={{ flex: 1, minWidth: 130, padding: '8px 12px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--bg)', color: 'var(--text)' }}
        >
          <option value="all">Wszystkie kategorie</option>
          {menu.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={filterAvail}
          onChange={e => setFilterAvail(e.target.value)}
          style={{ flex: 1, minWidth: 130, padding: '8px 12px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--bg)', color: 'var(--text)' }}
        >
          <option value="all">Wszystkie statusy</option>
          <option value="available">Dostępne</option>
          <option value="unavailable">Niedostępne</option>
        </select>
        {(search || filterCat !== 'all' || filterAvail !== 'all') && (
          <button className="btn-sm" onClick={() => { setSearch(''); setFilterCat('all'); setFilterAvail('all'); }}>
            Wyczyść filtry
          </button>
        )}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          {totalItems} pozycji
        </span>
      </div>

      {filteredMenu.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
          Brak wyników dla podanych filtrów
        </div>
      )}

      {filteredMenu.map(cat => (
        <div key={cat.id} style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <h3 style={{ fontSize: 15, fontWeight: 500 }}>{cat.name}</h3>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{cat.items.length} poz.</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button className="btn-sm" onClick={() => setEditCat({ id: cat.id, name: cat.name, sort: cat.sort, color: cat.color || '#185FA5' })}>Edytuj</button>
              <button className="btn-sm danger" onClick={() => deleteCat(cat)}>Usuń</button>
            </div>
          </div>
          <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {cat.items.length === 0 && (
              <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--text-muted)' }}>Brak pozycji</div>
            )}
            {cat.items.map((item, i) => (
              <div key={item.id} style={{
                padding: '11px 16px',
                borderBottom: i < cat.items.length - 1 ? '0.5px solid var(--border)' : 'none',
                opacity: item.available ? 1 : 0.5,
                background: item.available ? 'transparent' : '#FAFAFA',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{item.name}</span>
                    {!item.available && (
                      <span style={{ marginLeft: 8, fontSize: 11, background: '#FCEBEB', color: 'var(--red)', padding: '2px 7px', borderRadius: 4 }}>niedostępne</span>
                    )}
                    {(item.variant_groups || []).length > 0 && (
                      <span style={{ marginLeft: 8, fontSize: 11, background: 'var(--blue-light)', color: 'var(--blue-dark)', padding: '2px 7px', borderRadius: 4 }}>
                        {item.variant_groups.length} gr. wariantów
                      </span>
                    )}
                    {item.item_type && item.item_type.includes('modifier') && (
                      <span style={{ marginLeft: 8, fontSize: 11, background: '#EAF3DE', color: 'var(--green)', padding: '2px 7px', borderRadius: 4 }}>modyfikator</span>
                    )}
                    {item.item_type && item.item_type.includes('sale') && item.item_type.includes('modifier') && (
                      <span style={{ marginLeft: 4, fontSize: 11, background: '#FDF3E7', color: 'var(--amber)', padding: '2px 7px', borderRadius: 4 }}>+ sprzedaż</span>
                    )}
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 500, minWidth: 64, textAlign: 'right' }}>
                    {Number(item.price).toFixed(2)} zł
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>
                      VAT {item.vat_rate !== undefined ? item.vat_rate : 23}%
                    </span>
                  </span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-sm" onClick={() => setEditItem({ id: item.id, name: item.name, price: item.price, category_id: item.category_id || cat.id, item_type: item.item_type || 'sale', vat_rate: item.vat_rate })}>Edytuj</button>
                    <button className="btn-sm" style={{ color: 'var(--blue-dark)', borderColor: '#B5D4F4' }} onClick={() => setEditVariants(item)}>Warianty</button>
                    <button className="btn-sm" style={{ color: '#6C3483', borderColor: '#C39BD3' }} onClick={() => setEditModGroups(item)}>Mod. grupy</button>
                    <button className={`btn-sm ${item.available ? 'danger' : 'success'}`} onClick={() => toggleAvailable(item)}>
                      {item.available ? 'Wyłącz' : 'Włącz'}
                    </button>
                    <button className="btn-sm danger" onClick={() => deleteItem(item)}>Usuń</button>
                  </div>
                </div>
                {(item.variant_groups || []).map(g => (
                  <div key={g.id} style={{ marginTop: 6, paddingLeft: 16, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 4 }}>{g.name}:</span>
                    {g.options.map(o => (
                      <span key={o.id} style={{ fontSize: 11, background: o.is_default ? 'var(--blue-light)' : 'var(--bg)', border: '0.5px solid var(--border)', borderRadius: 4, padding: '2px 8px' }}>
                        {!!o.is_default && '★ '}{o.name} – {Number(o.price).toFixed(2)} zł
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}

      {editItem && (
        <Modal title={editItem.id ? 'Edytuj pozycję' : 'Nowa pozycja'} onClose={() => setEditItem(null)}>
          <ItemForm initial={editItem} categories={menu} onSave={saveItem} onCancel={() => setEditItem(null)} saving={saving} />
        </Modal>
      )}
      {editCat && (
        <Modal title={editCat.id ? 'Edytuj kategorię' : 'Nowa kategoria'} onClose={() => setEditCat(null)}>
          <CatForm initial={editCat} onSave={saveCat} onCancel={() => setEditCat(null)} saving={saving} />
        </Modal>
      )}
      {editVariants && (
        <VariantsModal item={editVariants} token={token} onClose={() => { setEditVariants(null); load(); }} showToast={showToast} />
      )}
      {editModGroups && (
        <ModGroupsModal item={editModGroups} token={token} onClose={() => { setEditModGroups(null); load(); }} showToast={showToast} />
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function VariantsModal({ item, token, onClose, showToast }) {
  const [groups, setGroups]     = useState(item.variant_groups || []);
  const [newGroup, setNewGroup] = useState(null);
  const [saving, setSaving]     = useState(false);

  async function addGroup(form) {
    setSaving(true);
    try {
      await fetch(`/api/menu/items/${item.id}/variants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const res  = await fetch(`/api/menu/items/${item.id}/variants`, { headers: { Authorization: `Bearer ${token}` } });
      setGroups(await res.json());
      setNewGroup(null);
      showToast('Dodano grupę wariantów');
    } finally { setSaving(false); }
  }

  async function updateGroup(groupId, form) {
    setSaving(true);
    try {
      await fetch(`/api/menu/variants/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const res  = await fetch(`/api/menu/items/${item.id}/variants`, { headers: { Authorization: `Bearer ${token}` } });
      setGroups(await res.json());
      showToast('Zapisano warianty');
    } finally { setSaving(false); }
  }

  async function deleteGroup(groupId) {
    await fetch(`/api/menu/variants/${groupId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setGroups(prev => prev.filter(g => g.id !== groupId));
    showToast('Usunięto grupę');
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 480, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500 }}>Warianty: {item.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>
        {groups.map(g => (
          <GroupEditor key={g.id} group={g} onSave={form => updateGroup(g.id, form)} onDelete={() => deleteGroup(g.id)} saving={saving} />
        ))}
        {groups.length === 0 && !newGroup && (
          <div style={{ padding: '16px 0', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>Brak wariantów — dodaj pierwszą grupę</div>
        )}
        {newGroup
          ? <GroupEditor group={newGroup} isNew onSave={addGroup} onDelete={() => setNewGroup(null)} saving={saving} />
          : <button className="btn-outline" style={{ width: '100%', marginTop: 12 }} onClick={() => setNewGroup({ name: '', required: 1, options: [{ name: '', price: '' }] })}>+ Dodaj grupę wariantów</button>
        }
      </div>
    </div>
  );
}

function GroupEditor({ group, isNew, onSave, onDelete, saving }) {
  const [form, setForm] = useState({
    name:     group.name || '',
    required: group.required !== undefined ? group.required : 1,
    options:  group.options?.length ? group.options.map(o => ({ id: o.id, name: o.name, price: o.price, is_default: !!o.is_default })) : [{ name: '', price: '', is_default: false }],
  });
  const [open, setOpen] = useState(isNew || false);

  function setOpt(i, k, v) { setForm(f => ({ ...f, options: f.options.map((o, idx) => idx === i ? { ...o, [k]: v } : o) })); }
  function setDefaultOpt(i) { setForm(f => ({ ...f, options: f.options.map((o, idx) => ({ ...o, is_default: idx === i })) })); }
  function addOpt() { setForm(f => ({ ...f, options: [...f.options, { name: '', price: '', is_default: false }] })); }
  function removeOpt(i) { setForm(f => ({ ...f, options: f.options.filter((_, idx) => idx !== i) })); }

  return (
    <div style={{ border: '0.5px solid var(--border)', borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', background: 'var(--bg)', cursor: 'pointer', gap: 10 }} onClick={() => setOpen(o => !o)}>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{form.name || 'Nowa grupa'}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{form.options.length} opcji</span>
        {form.required ? <span style={{ fontSize: 11, background: '#FCEBEB', color: 'var(--red)', padding: '2px 7px', borderRadius: 4 }}>wymagana</span> : null}
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: 14 }}>
          <div className="field"><label>Nazwa grupy (np. Rozmiar, Mleko)</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Rozmiar" /></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.required} onChange={e => setForm(f => ({ ...f, required: e.target.checked ? 1 : 0 }))} />
            Wybór obowiązkowy
          </label>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 8 }}>Opcje</div>
          {form.options.map((opt, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} title="Domyślna opcja">
                <input type="radio" name={`default-${group.id || 'new'}`} checked={!!opt.is_default} onChange={() => setDefaultOpt(i)} />
              </label>
              <input value={opt.name} onChange={e => setOpt(i, 'name', e.target.value)} placeholder="np. Mały" style={{ flex: 2 }} />
              <input type="number" step="0.5" min="0" value={opt.price} onChange={e => setOpt(i, 'price', e.target.value)} placeholder="10.00" style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 30 }}>zł</span>
              {form.options.length > 1 && <button className="btn-sm danger" onClick={() => removeOpt(i)}>✕</button>}
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -4, marginBottom: 10 }}>Zaznacz kółkiem opcję domyślnie wybraną przy dodawaniu produktu do zamówienia.</div>
          <button className="btn-sm" onClick={addOpt} style={{ marginBottom: 14 }}>+ Opcja</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-sm danger" onClick={onDelete}>{isNew ? 'Anuluj' : 'Usuń grupę'}</button>
            <button className="btn-primary" style={{ flex: 1 }} onClick={() => onSave(form)} disabled={saving || !form.name || form.options.some(o => !o.name || !o.price)}>
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 380, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ItemForm({ initial, categories, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name:        initial.name || '',
    price:       initial.price || '',
    category_id: initial.category_id || categories[0]?.id || '',
    item_type:   initial.item_type || 'sale',
    vat_rate:    initial.vat_rate !== undefined ? String(parseInt(initial.vat_rate)) : '23',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  React.useEffect(() => {
    setForm({
      name:        initial.name || '',
      price:       initial.price || '',
      category_id: initial.category_id || categories[0]?.id || '',
      item_type:   initial.item_type || 'sale',
      vat_rate:    initial.vat_rate !== undefined ? String(parseInt(initial.vat_rate)) : '23',
    });
  }, [initial.id]);

  function toggleType(val) {
    const current = form.item_type.split(',').filter(Boolean);
    const updated = current.includes(val)
      ? current.filter(t => t !== val)
      : [...current, val];
    set('item_type', updated.join(',') || 'sale');
  }

  const types = form.item_type.split(',');

  return (
    <div>
      <div className="field"><label>Nazwa</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="np. Cappuccino" /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
        <div className="field">
          <label>Cena bazowa (zł)</label>
          <input type="number" step="0.5" min="0" value={form.price} onChange={e => set('price', e.target.value)} placeholder="12.00" />
          {form.price > 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Podgląd: {Number(form.price).toFixed(2)} zł</div>}
        </div>
        <div className="field">
          <label>Stawka VAT</label>
          <select value={form.vat_rate} onChange={e => set('vat_rate', e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 14, background: 'var(--surface)', color: 'var(--text)' }}>
            <option value="23">23%</option>
            <option value="8">8%</option>
            <option value="5">5%</option>
            <option value="0">ZW (0%)</option>
          </select>
        </div>
      </div>
      <div className="field">
        <label>Kategoria</label>
        <select value={form.category_id} onChange={e => set('category_id', e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 14, background: 'var(--surface)', color: 'var(--text)' }}>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label>Funkcja pozycji</label>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {[{val:'sale',label:'Pozycja sprzedażowa'},{val:'modifier',label:'Modyfikator'}].map(opt => (
            <label key={opt.val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '8px 12px', border: `0.5px solid ${types.includes(opt.val) ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 6, background: types.includes(opt.val) ? 'var(--blue-light)' : 'none' }}>
              <input type="checkbox" checked={types.includes(opt.val)} onChange={() => toggleType(opt.val)} />
              {opt.label}
            </label>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
          Możesz zaznaczyć obie opcje jeśli produkt pełni obie funkcje.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn-outline" onClick={onCancel} style={{ flex: 1 }}>Anuluj</button>
        <button className="btn-primary" style={{ flex: 2 }} onClick={() => onSave({ ...form, id: initial.id })} disabled={saving || !form.name || form.price === ''}>
          {saving ? 'Zapisywanie...' : 'Zapisz'}
        </button>
      </div>
    </div>
  );
}

const CAT_COLORS = [
  '#185FA5', '#3B6D11', '#6B3FA0', '#A32D2D', '#854F0B',
  '#0D7377', '#C2185B', '#00695C', '#1565C0', '#4E342E',
];

function CatForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ name: initial.name || '', sort: initial.sort || '', color: initial.color || '#185FA5' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div>
      <div className="field"><label>Nazwa kategorii</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="np. Desery" /></div>
      <div className="field"><label>Kolejność</label><input type="number" min="1" value={form.sort} onChange={e => set('sort', e.target.value)} placeholder="5" /></div>
      <div className="field">
        <label>Kolor</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          {CAT_COLORS.map(c => (
            <button key={c} type="button" onClick={() => set('color', c)}
              style={{
                width: 28, height: 28, borderRadius: '50%', background: c, border: 'none',
                cursor: 'pointer', outline: form.color === c ? `3px solid ${c}` : 'none',
                outlineOffset: 2, flexShrink: 0,
              }} />
          ))}
          <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
            style={{ width: 36, height: 28, borderRadius: 6, border: '0.5px solid var(--border)', cursor: 'pointer', padding: 2 }} />
        </div>
        <div style={{ padding: '7px 12px', borderRadius: 8, background: form.color + '18', border: `1px solid ${form.color}40`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: form.color }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: form.color }}>{form.name || 'Podgląd'}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn-outline" onClick={onCancel} style={{ flex: 1 }}>Anuluj</button>
        <button className="btn-primary" style={{ flex: 2 }} onClick={() => onSave({ ...form, id: initial.id })} disabled={saving || !form.name}>
          {saving ? 'Zapisywanie...' : 'Zapisz'}
        </button>
      </div>
    </div>
  );
}

function ModGroupsModal({ item, token, onClose, showToast }) {
  const [allGroups, setAllGroups]       = useState([]);
  const [assignedIds, setAssignedIds]   = useState([]);
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    Promise.all([
      fetch('/api/modifier-groups', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`/api/modifier-groups/for-item/${item.id}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]).then(([all, assigned]) => {
      setAllGroups(all);
      setAssignedIds(assigned.map(g => g.id));
    });
  }, [item.id, token]);

  function toggle(id) {
    setAssignedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }

  async function save() {
    setSaving(true);
    try {
      await fetch(`/api/modifier-groups/assign/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ group_ids: assignedIds }),
      });
      showToast('Zapisano przypisanie grup');
      onClose();
    } catch (err) { showToast('Błąd: ' + err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 420, maxWidth: '95vw', maxHeight: '80vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500 }}>Grupy modyfikatorów: {item.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        {allGroups.length === 0 && (
          <div style={{ padding: '16px 0', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
            Brak grup — najpierw utwórz grupy modyfikatorów w zakładce "Mod. grupy"
          </div>
        )}

        {allGroups.map(group => (
          <label key={group.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
            border: `0.5px solid ${assignedIds.includes(group.id) ? '#6C3483' : 'var(--border)'}`,
            borderRadius: 8, marginBottom: 8, cursor: 'pointer',
            background: assignedIds.includes(group.id) ? '#F5EEF8' : 'none',
          }}>
            <input type="checkbox" checked={assignedIds.includes(group.id)} onChange={() => toggle(group.id)} style={{ marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{group.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {group.items.map(i => i.name).join(', ')}
              </div>
            </div>
            {group.required ? <span style={{ fontSize: 11, background: '#FCEBEB', color: 'var(--red)', padding: '2px 6px', borderRadius: 4 }}>wymagana</span> : null}
          </label>
        ))}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn-outline" onClick={onClose} style={{ flex: 1 }}>Anuluj</button>
          <button className="btn-primary" style={{ flex: 2 }} onClick={save} disabled={saving}>
            {saving ? 'Zapisywanie...' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}
